import { CrudStoreItemOnboarding } from 'legacy/lut/classes/models/onboarding/CrudStoreItemOnboarding'
import { ICollectorData } from 'legacy/lut/components/widgets/public/onboarding/requestsInfo/requestDetail/actionView/dataCollector/ICollectorData'

import { Logic, Operator } from 'core-components/types'
import WebSocketStore from 'core/classes/services/WebSocketStore'
import { CrudStore } from 'core/types/crudStore'
import settings from 'src/config/settings'

export enum CollectedDataCacheKey {
  DataCollector = 'DataCollector',
  ExceptionForm = 'ExceptionForm',
  LocalStoreList = 'LocalStoreList',
}

export type TCollectedDataFetchOptions = {
  cacheKey: CollectedDataCacheKey
  needCache?: boolean
  pagination?: {
    page: number
    pageSize: number
  }
}

class CollectedDataApi {
  public readonly FORM_ID = 677
  public readonly FORM_CODE = 'CollectedDataForm'

  public getCrud(cacheKey?: CollectedDataCacheKey) {
    return CrudStore.get(CrudStoreItemOnboarding.CollectedData, cacheKey)
  }

  public delete(item: ICollectorData) {
    return this.getCrud().delete(this.FORM_ID, item)
  }

  public create(items: ICollectorData[]) {
    return this.getCrud().create(this.FORM_ID, items)
  }

  public update(items: ICollectorData[]) {
    return this.getCrud().update(this.FORM_ID, items)
  }

  public async getManyByRequestId(
    requestId: number,
    { cacheKey, needCache }: TCollectedDataFetchOptions
  ) {
    const crud = this.getCrud(cacheKey)
    crud.setFilter({
      logic: Logic.AND,
      filters: [
        {
          field: 'RequestId',
          operator: Operator.EQUAL,
          value: requestId,
        },
      ],
    })
    const result = await crud.fetch({ force: !needCache, skipTotal: true })

    return result.items as ICollectorData[]
  }

  public async getRequestFilesRows(
    requestId: number,
    field: string,
    { cacheKey, needCache, pagination }: TCollectedDataFetchOptions
  ) {
    const crud = this.getCrud(cacheKey)
    crud.setFilter({
      logic: Logic.AND,
      filters: [
        {
          field: 'RequestId',
          operator: Operator.EQUAL,
          value: requestId,
        },
        {
          field: 'Type',
          operator: Operator.EQUAL,
          value: 'file',
        },
        {
          field: 'Field',
          operator: Operator.EQUAL,
          value: field,
        },
      ],
    })
    if (pagination) {
      crud.setPaging({
        page: pagination.page,
        pageSize: pagination.pageSize,
      })
    }
    const result = await crud.fetch({ force: !needCache, skipTotal: true })

    return result.items as ICollectorData[]
  }

  public async getSyncedWithParent(requestId: number, actionUUID: string) {
    const result: { items: ICollectorData[]; error?: string } = {
      items: [],
      error: undefined,
    }
    try {
      const response = await WebSocketStore.get('onboarding').call<{ items: ICollectorData[] }>({
        service: 'collectedData',
        action: 'getSyncedWithParent',
        payload: { requestId, actionUUID },
        withoutTokenRefresh: true,
        isAuth: true,
      })
      if (response.status !== 'success') {
        result.error = response.message || 'Failed'
      } else {
        result.items = response.data.items
      }
    } catch (e: any) {
      if (typeof e === 'string') {
        result.error = e
      } else {
        result.error = e.message || 'Failed'
      }
    }

    return result
  }

  public async saveAndSyncWithParent(
    { requestId, actionUUID, items }: ISaveCollectedDataParams,
    accessToken: string
  ) {
    const url = `${settings.httpsServices.onboarding}/api/v0/collectedData/saveAndSyncWithParent`

    const formData = new FormData()
    formData.set('requestId', requestId.toString())
    formData.set('actionUUID', actionUUID)
    formData.set('models', JSON.stringify(items.map(({ file, ...other }) => other)))
    items
      .filter((i) => i.Type === 'file' && i.file && i.Field)
      .forEach((i, index) => {
        const file = i.file as File
        formData.append(`${i.Field}-${index}`, file, file.name)
      })

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: accessToken,
      },
      body: formData,
    })
    if (!response.ok) {
      const errorMessage = (await response.text()) || 'Failed to save field'

      throw new Error(errorMessage)
    }

    const result: { items: ICollectorData[] } = await response.json()

    return result.items
  }

  public async fetchLastRequestReject(requestId: number) {
    const response = await WebSocketStore.get('onboarding').call<{ item: ICollectorData | null }>({
      service: 'collectedData',
      action: 'fetchLastRequestReject',
      payload: { requestId },
      withoutTokenRefresh: true,
      isAuth: true,
    })
    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed to fetch last request rejection')
    }

    return response.data.item
  }
}

export const collectedDataApi = new CollectedDataApi()

export interface ISaveCollectedDataParams {
  requestId: number
  actionUUID: string
  items: (Partial<ICollectorData> & { file?: File })[]
}
